import { useEffect, useRef, useState, type FormEvent } from 'react';

import { adminApiMessage, evalsApi } from '../adminApi';
import { agentLabel } from '../evals/agents';
import { CaseMarkerChips, CaseOutcome, EvalBadge } from '../evals/EvalBits';
import {
  definitionView,
  drawerVisibility,
  repeatMark,
  repeatMarkText,
  revealReason,
  sortedRepeats,
  splitLabel,
} from '../evals/caseTable';
import {
  formatMs,
  formatUsd,
  surfaceLabel,
  transcriptStateText,
  verdictSourceText,
} from '../evals/format';
import {
  isPastLimit,
  noteActionText,
  noteAreaText,
  noteKindText,
  noteOutcomeText,
  noteReasonText,
  notesCountText,
  noteWho,
  showNotesSection,
} from '../evals/proposedNotes';
import type {
  EvalCaseRow,
  EvalProposedNote,
  EvalRepeatView,
  EvalRevealedResult,
  EvalTranscriptView,
} from '../evals/types';
import { formatDate } from '../format';

// The case drawer on a run's page (task eval_admin_runs): why this case passed
// or failed. The prompt, each repeat's reply, tool trace and judge reasons, and
// the case's rubric and facts as the deployed corpus holds them, flagged when
// that definition has changed since the run.
//
// A held-out test-split case (ADR 0232 §15, neo_eval_split) comes back with
// its prompt, replies, reasons and definition withheld. Showing one repeat is
// an explicit click; the server writes an admin_eval_reveal_test audit row
// before it answers, and nothing is shown if it cannot.

function json(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function Transcript({ t }: { t: EvalTranscriptView }) {
  return (
    <details className="eval-drawer-details">
      <summary>
        Transcript ({t.messages.length} messages{t.truncated ? ', truncated' : ''})
      </summary>
      {t.contextText !== null ? (
        <>
          <h5>Context the model saw</h5>
          <pre className="eval-pre">{t.contextText}</pre>
        </>
      ) : null}
      <h5>Messages</h5>
      <pre className="eval-pre">{json(t.messages)}</pre>
      {t.toolResults.length > 0 ? (
        <>
          <h5>Tool results</h5>
          <pre className="eval-pre">{json(t.toolResults)}</pre>
        </>
      ) : null}
      {t.rawOutput !== null ? (
        <>
          <h5>Raw output</h5>
          <pre className="eval-pre">{t.rawOutput}</pre>
        </>
      ) : null}
    </details>
  );
}

/** What a note cited: the readings and the tools that read them, or the owner's quoted words. */
function NoteCites({ n }: { n: EvalProposedNote }) {
  if (n.basis !== null && (n.basis.paths.length > 0 || n.basis.tools.length > 0)) {
    return (
      <p className="eval-note-cites">
        {n.basis.paths.length > 0 ? (
          <span>
            Cited{' '}
            {n.basis.paths.map((p, i) => (
              <span key={`${p}-${i}`}>
                {i > 0 ? ', ' : ''}
                <code>{p}</code>
              </span>
            ))}
          </span>
        ) : null}
        {n.basis.tools.length > 0 ? (
          <span>
            Read with{' '}
            {n.basis.tools.map((t, i) => (
              <span key={`${t}-${i}`}>
                {i > 0 ? ', ' : ''}
                <code>{t}</code>
              </span>
            ))}
          </span>
        ) : null}
      </p>
    );
  }
  if (n.ownerWords !== null) {
    return (
      <p className="eval-note-cites">
        <span>Quoted the owner: &ldquo;{n.ownerWords}&rdquo;</span>
      </p>
    );
  }
  return <p className="eval-note-cites admin-muted">Cited nothing.</p>;
}

/** The boat notes a repeat's turn proposed (TOM-616), and what the notebook did with each. */
function ProposedNotes({ notes }: { notes: EvalProposedNote[] }) {
  return (
    <>
      <h5>Boat notes proposed</h5>
      {notes.length === 0 ? (
        <p className="admin-muted" data-testid="eval-notes-none">
          None: the turn proposed no notes.
        </p>
      ) : (
        <>
          <p className="admin-muted eval-sub">{notesCountText(notes)}</p>
          <ul className="eval-notes" data-testid="eval-notes">
            {notes.map((n, i) => {
              if (isPastLimit(n)) {
                return (
                  <li key={i} className="eval-note-more admin-muted" data-testid="eval-notes-more">
                    {n.reason}
                  </li>
                );
              }
              const outcome = noteOutcomeText(n);
              const area = noteAreaText(n.area);
              const kind = noteKindText(n.kind);
              return (
                <li
                  key={i}
                  className="eval-note"
                  data-testid={`eval-note-${i + 1}`}
                  data-outcome={n.outcome}
                >
                  <div className="eval-note-head">
                    <EvalBadge tone={outcome.tone}>{outcome.label}</EvalBadge>
                    <span className="eval-note-action">{noteActionText(n.action)}</span>
                    <span className="admin-muted">from {noteWho(n)}</span>
                  </div>
                  {area !== null || kind !== null || n.subject !== null ? (
                    <p className="eval-note-key">
                      {[area, kind].filter((x) => x !== null).join(' · ')}
                      {n.subject !== null ? (
                        <>
                          {area !== null || kind !== null ? ' · ' : ''}
                          <code>{n.subject}</code>
                        </>
                      ) : area !== null ? (
                        ' · the part as a whole'
                      ) : null}
                    </p>
                  ) : null}
                  {n.text !== null && n.text.trim().length > 0 ? (
                    <p className="eval-note-text">{n.text}</p>
                  ) : null}
                  {n.outcome === 'refused' ? (
                    <p className="eval-note-reason" data-testid={`eval-note-reason-${i + 1}`}>
                      Why: {noteReasonText(n)}
                    </p>
                  ) : null}
                  {n.action !== null ? <NoteCites n={n} /> : null}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </>
  );
}

function RepeatDetail({
  r,
  hidden,
  definition,
}: {
  r: EvalRepeatView;
  hidden: boolean;
  definition: Record<string, unknown> | null | undefined;
}) {
  const mark = repeatMark(r);
  return (
    <div className="eval-repeat-detail" data-testid={`eval-repeat-${r.repeat}`}>
      <div className="eval-repeat-head">
        <span className={`eval-repeat eval-repeat--${mark}`} aria-hidden="true" />
        <strong>Repeat {r.repeat}</strong>
        <span className="admin-muted">{repeatMarkText(mark)}</span>
        {r.decisionPassed !== null ? (
          <span className={r.decisionPassed ? 'admin-muted' : 'eval-error-text'}>
            {r.decisionPassed ? 'decision matched' : 'decision did not match'}
          </span>
        ) : null}
        <span className="admin-muted eval-sub">{formatDate(r.collectedAt)}</span>
      </div>

      {r.error !== null ? <p className="eval-error-text">{r.error}</p> : null}
      {r.blocked !== null ? (
        <p className="admin-muted">Blocked by output validation: {r.blocked}</p>
      ) : null}
      {r.blockedVisibility ? (
        <p className="admin-muted">
          Could not see: {r.visibilityMissing.length > 0 ? r.visibilityMissing.join(', ') : '—'}
        </p>
      ) : null}

      <h5>Reply</h5>
      {hidden ? (
        <p className="eval-hidden-text">Hidden: a test-split reply.</p>
      ) : r.reply !== null && r.reply.length > 0 ? (
        <div className="eval-reply" data-testid="eval-reply">
          {r.reply}
          {r.replyTruncated ? <span className="admin-muted"> … (truncated)</span> : null}
        </div>
      ) : (
        <p className="admin-muted">No reply recorded.</p>
      )}
      {r.watchDecision !== null && r.watchDecision !== undefined && !hidden ? (
        <>
          <h5>Watch decision</h5>
          <pre className="eval-pre">{json(r.watchDecision)}</pre>
        </>
      ) : null}

      <h5>Verdicts</h5>
      {r.verdicts.length === 0 ? (
        <p className="admin-muted">{verdictSourceText(r.verdictSource)}</p>
      ) : (
        <ul className="eval-verdicts">
          {r.verdicts.map((v, i) => (
            <li key={`${v.judgeModel ?? 'none'}-${v.grading}-${i}`}>
              <span>
                {verdictSourceText(v.source)}
                {v.judgeModel !== null ? (
                  <>
                    {' '}
                    <code>{v.judgeModel}</code>
                  </>
                ) : null}
                {v.grading === 2 ? <span className="admin-muted"> (second grading)</span> : null}
                {v.met !== null ? ` · ${v.met ? 'met' : 'not met'}` : ''}
                {v.declined !== null ? ` · ${v.declined ? 'declined' : 'answered'}` : ''}
              </span>
              {hidden ? (
                <span className="eval-hidden-text">Reason hidden.</span>
              ) : v.reason !== null && v.reason.length > 0 ? (
                <span className="eval-reason">{v.reason}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {hidden ? (
        r.notesHidden === true ? (
          <>
            <h5>Boat notes proposed</h5>
            <p className="eval-hidden-text" data-testid="eval-notes-hidden">
              Hidden: a test-split result&apos;s notes.
            </p>
          </>
        ) : null
      ) : showNotesSection(r.notes, definition) ? (
        <ProposedNotes notes={r.notes ?? []} />
      ) : null}

      <h5>Tool trace</h5>
      {r.toolCalls.length === 0 ? (
        <p className="admin-muted">No tool calls.</p>
      ) : (
        <ol className="eval-tools">
          {r.toolCalls.map((c) => (
            <li key={c.seq}>
              <code>{c.tool}</code>
              {c.isError === true ? (
                <EvalBadge tone="bad" title="The tool returned an error">
                  error
                </EvalBadge>
              ) : null}
              {hidden || c.args === null ? null : (
                <pre className="eval-pre eval-pre--small">{json(c.args)}</pre>
              )}
            </li>
          ))}
        </ol>
      )}

      <dl className="eval-kv">
        <dt>Served by</dt>
        <dd>
          {r.servedModel ?? '—'}
          {r.upstreamProviders.length > 0 ? ` via ${r.upstreamProviders.join(', ')}` : ''}
        </dd>
        <dt>Finish</dt>
        <dd>{r.finishReason ?? '—'}</dd>
        <dt>Latency</dt>
        <dd>{formatMs(r.latencyMs)}</dd>
        <dt>Model calls</dt>
        <dd>
          {r.modelCalls}
          {r.invalidToolCalls > 0 ? ` (${r.invalidToolCalls} invalid tool calls)` : ''}
          {!r.calledModel && r.localDecision !== null
            ? ` · settled locally (${r.localDecision})`
            : ''}
        </dd>
        <dt>Cost</dt>
        <dd>
          {formatUsd(r.costUsd)} model · {formatUsd(r.judgeCostUsd)} judge
        </dd>
        <dt>Transcript</dt>
        <dd>{transcriptStateText(r.transcriptState)}</dd>
      </dl>
      {r.transcript !== undefined && r.transcript !== null && !hidden ? (
        <Transcript t={r.transcript} />
      ) : null}
    </div>
  );
}

function Definition({
  def,
  hidden,
}: {
  def: Record<string, unknown> | null | undefined;
  hidden: boolean;
}) {
  if (def === undefined) return <p className="admin-loading-row">Loading the case…</p>;
  if (def === null) {
    return <p className="admin-muted">The deployed corpus no longer holds this case.</p>;
  }
  const d = definitionView(def);
  return (
    <div className="eval-definition" data-testid="eval-definition">
      {d.title !== null ? <p className="eval-title">{d.title}</p> : null}
      {d.expect !== null ? (
        <p>
          <span className="admin-muted">Expected: </span>
          <code>{d.expect}</code>
        </p>
      ) : null}
      {d.source !== null ? (
        <p>
          <span className="admin-muted">Source: </span>
          {d.source}
        </p>
      ) : null}
      {d.moment !== null ? (
        <p>
          <span className="admin-muted">Moment: </span>
          {d.moment}
        </p>
      ) : null}
      {d.expectedTools.length > 0 ? (
        <p>
          <span className="admin-muted">Must call: </span>
          {d.expectedTools.join(', ')}
        </p>
      ) : null}
      <h5>Rubric</h5>
      {hidden || d.redacted ? (
        <p className="eval-hidden-text">Hidden: a test-split rubric.</p>
      ) : d.rubric !== null ? (
        <div className="eval-reply">{d.rubric}</div>
      ) : (
        <p className="admin-muted">This case has no rubric: its expectation above grades it.</p>
      )}
      {d.facts.length > 0 ? (
        <>
          <h5>Facts</h5>
          <ul className="eval-facts">
            {d.facts.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </>
      ) : hidden || d.redacted ? (
        <p className="eval-hidden-text">Facts hidden.</p>
      ) : null}
      {d.note !== null ? (
        <p className="admin-muted">
          <span>Note: </span>
          {d.note}
        </p>
      ) : null}
      <details className="eval-drawer-details">
        <summary>The deployed case as stored</summary>
        <pre className="eval-pre eval-pre--small">{json(def)}</pre>
      </details>
    </div>
  );
}

export function EvalCaseDrawer({
  runId,
  row,
  onClose,
}: {
  runId: string;
  row: EvalCaseRow;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<EvalCaseRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<ReadonlyMap<string, EvalRevealedResult>>(new Map());
  const [reason, setReason] = useState('');
  const [revealing, setRevealing] = useState<string | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const { model, role, caseId } = row;
  useEffect(() => {
    let ignore = false;
    setDetail(null);
    setError(null);
    setRevealed(new Map());
    setRevealError(null);
    evalsApi
      .caseDetail(runId, { model, role, caseId })
      .then((res) => {
        if (!ignore) setDetail(res.cases[0] ?? null);
      })
      .catch((err) => {
        if (!ignore) setError(adminApiMessage(err, 'Failed to load this case.'));
      });
    return () => {
      ignore = true;
    };
  }, [runId, model, role, caseId]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const shown = detail ?? row;
  const hidden = drawerVisibility(shown) === 'hidden';
  const firstReveal = [...revealed.values()][0];

  async function reveal(e: FormEvent, resultId: string) {
    e.preventDefault();
    setRevealing(resultId);
    setRevealError(null);
    try {
      const res = await evalsApi.reveal(resultId, revealReason(reason));
      setRevealed((prev) => new Map(prev).set(resultId, res));
    } catch (err) {
      setRevealError(adminApiMessage(err, 'The reveal failed, so nothing is shown.'));
    } finally {
      setRevealing(null);
    }
  }

  const prompt = firstReveal?.prompt ?? shown.prompt;
  const definition = firstReveal !== undefined ? firstReveal.definition : shown.definition;

  return (
    <div className="eval-drawer-root">
      <div className="eval-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <aside
        className="eval-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="eval-drawer-title"
        data-testid="eval-case-drawer"
      >
        <header className="eval-drawer-header">
          <div>
            <h2 id="eval-drawer-title">
              <code>{row.caseId}</code>
            </h2>
            <p className="admin-muted eval-sub">
              {surfaceLabel(row.surface)} · <code>{row.model}</code>
              {row.role !== 'chat' ? ` · ${agentLabel(row.role)}` : ''}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="admin-button-secondary"
            onClick={onClose}
            aria-label="Close the case"
          >
            Close
          </button>
        </header>

        <div className="eval-drawer-body">
          <div className="eval-drawer-summary">
            <CaseOutcome row={shown} />
            <CaseMarkerChips row={shown} />
          </div>

          {shown.caseChanged === true ? (
            <p className="eval-callout" data-testid="eval-case-changed">
              Case changed since this run: the deployed definition (or its moment) differs from the
              one this run graded. The rubric below is today&apos;s.
            </p>
          ) : null}

          <dl className="eval-kv">
            <dt>Split</dt>
            <dd data-testid="eval-drawer-split">
              {splitLabel(shown) ?? (shown.suite === 'short' ? 'none (short suite)' : '—')}
              {shown.splitSource === 'demo' ? (
                <span className="admin-muted"> · the METS demo set, in neither split</span>
              ) : shown.splitSource !== null ? (
                <span className="admin-muted"> · {shown.splitSource}</span>
              ) : null}
            </dd>
            <dt>Category</dt>
            <dd>{shown.category ?? '—'}</dd>
            <dt>Case hash</dt>
            <dd>
              <code>{shown.caseHash ?? 'not recorded'}</code>
            </dd>
          </dl>

          {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}

          {hidden ? (
            <div className="eval-hidden-panel" data-testid="eval-hidden-panel">
              <p>
                <strong>A held-out test case.</strong> Its prompt, replies, reasons and rubric stay
                hidden so nobody tunes Neo or an expert to them. Revealing a repeat shows it in full
                and is logged with your account.
              </p>
              <label className="eval-reveal-reason">
                Reason (optional)
                <input
                  type="text"
                  value={reason}
                  maxLength={200}
                  placeholder="e.g. checking a judge disagreement"
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="eval-reveal-reason"
                />
              </label>
              {revealError !== null ? (
                <div className="admin-auth-banner error">{revealError}</div>
              ) : null}
            </div>
          ) : null}

          <h3>Prompt</h3>
          {prompt !== null && prompt !== undefined ? (
            <div className="eval-reply" data-testid="eval-prompt">
              {prompt}
            </div>
          ) : hidden ? (
            <p className="eval-hidden-text">Hidden: a test-split prompt.</p>
          ) : (
            <p className="admin-muted">Not recorded.</p>
          )}

          <h3>The case</h3>
          <Definition
            def={detail === null && firstReveal === undefined ? undefined : definition}
            hidden={hidden && firstReveal === undefined}
          />

          <h3>Repeats</h3>
          {sortedRepeats(shown).map((r) => {
            const full = revealed.get(r.resultId)?.repeat;
            const fromDetail = detail?.repeats.find((x) => x.resultId === r.resultId);
            const view = full ?? fromDetail ?? r;
            return (
              <div key={r.resultId}>
                {hidden && full === undefined ? (
                  <form className="eval-reveal" onSubmit={(e) => void reveal(e, r.resultId)}>
                    <button
                      type="submit"
                      className="admin-button-secondary"
                      disabled={revealing !== null}
                      data-testid={`eval-reveal-${r.repeat}`}
                    >
                      {revealing === r.resultId ? 'Revealing…' : `Reveal repeat ${r.repeat}`}
                    </button>
                  </form>
                ) : null}
                {full !== undefined ? (
                  <p className="eval-callout" data-testid={`eval-revealed-${r.repeat}`}>
                    Revealed. This access is logged.
                  </p>
                ) : null}
                <RepeatDetail
                  r={view}
                  hidden={hidden && full === undefined}
                  definition={definition}
                />
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
}
